import { useEffect, useState } from "react";
import apiClient from "@/lib/apiClient";
import { Heartbeat, Warning, Rocket, TrendUp, Lifebuoy, ShieldCheck, Info } from "@phosphor-icons/react";

export const pct = (v) => (v === null || v === undefined ? "N/A" : `${v}%`);

function Card({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</span>
        <Icon size={18} color={accent} weight="bold" />
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-3 text-[#0F172A]">{value ?? "N/A"}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export function HealthCards({ data }) {
  const c = data.cards;
  const a = data.activity;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="health-cards">
      <Card testId="health-card-healthy" label="Healthy" value={c.healthy} sub={`Avg score ${c.avg_health ?? "N/A"}`} icon={Heartbeat} accent="#059669" />
      <Card testId="health-card-at-risk" label="At Risk" value={c.at_risk} sub={`${c.watch} on watch`} icon={Warning} accent="#DC2626" />
      <Card testId="health-card-onboarding" label="Onboarding" value={c.needs_onboarding} sub="No analysis started" icon={Rocket} accent="#7C3AED" />
      <Card testId="health-card-upgrade" label="Upgrade" value={c.upgrade} sub="80%+ of allowance" icon={TrendUp} accent="#2563EB" />
      <Card testId="health-card-support" label="Support" value={c.support} sub="Failures / past due" icon={Lifebuoy} accent="#D97706" />
      <Card testId="health-card-active" label={`Active (${data.window}d)`} value={a.active_customers} sub={`Completed analyses: ${a.analyses_completed}`} icon={ShieldCheck} accent="#0891B2" />
    </div>
  );
}

export function EventStrip({ data }) {
  const a = data.activity;
  const items = [
    ["Checkouts started", a.checkout_started], ["Checkouts completed", a.checkout_completed],
    ["Reports viewed", a.report_viewed], ["Reports downloaded", a.report_downloaded],
    ["Upload failures", a.upload_failed], ["Analysis failures", a.analysis_failed],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="health-event-strip">
      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        {items.map(([l, v]) => (
          <div key={l}><span className="text-slate-400">{l}:</span> <span className="font-semibold text-[#0F172A]">{v}</span></div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        {data.events_tracked_since
          ? `Operational events tracked since ${new Date(data.events_tracked_since).toLocaleDateString()} — earlier activity is not backfilled.`
          : "No operational events recorded yet — counts start from this release (no backfill)."}
      </p>
    </div>
  );
}

export function SegmentGuide({ segments, active, onPick }) {
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="segment-guide">
      {segments.map((s) => (
        <button key={s.key} type="button" onClick={() => onPick(active === s.key ? "" : s.key)} data-testid={`segment-chip-${s.key}`}
          className={`text-left rounded-md border px-3 py-2 transition-colors ${active === s.key ? "border-[#0F172A] bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#0F172A]"><span className="text-slate-300 font-mono-plex text-[10px] mr-1.5">#{s.precedence}</span>{s.label}</span>
            <span className="font-display font-bold text-[#0F172A]" data-testid={`segment-count-${s.key}`}>{s.count}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{s.definition}</div>
        </button>
      ))}
    </div>
  );
}

export function ScoringDoc({ scoring }) {
  return (
    <details className="text-sm" data-testid="scoring-doc">
      <summary className="cursor-pointer text-slate-500 hover:text-[#0F172A] inline-flex items-center gap-1.5"><Info size={14} /> How the health score works</summary>
      <table className="mt-3 w-full text-xs">
        <tbody>
          {scoring.weights.map((w) => (
            <tr key={w.key} className="border-b border-slate-100">
              <td className="py-1.5 pr-3 font-semibold text-[#0F172A] whitespace-nowrap">{w.label}</td>
              <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{w.max} pts</td>
              <td className="py-1.5 text-slate-500">{w.rule}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 mt-2">Bands: Healthy {scoring.bands.healthy} · Watch {scoring.bands.watch} · At risk {scoring.bands.at_risk}. Segments use the numbered precedence — the first match is the primary label.</p>
    </details>
  );
}

export function useHealthSummary(window) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    apiClient.get(`/owner/health/summary?window=${window}`)
      .then((r) => { setData(r.data); setError(false); })
      .catch(() => setError(true));
  }, [window]);
  return { data, error };
}
